import { SearchScope } from './search-scope';

/**
 * Die Angaben eines Beraters, aufgeteilt nach den Bereichen der Suche. Die Übersicht stellt
 * nur zusammen, was der gewählte Bereich braucht; alles andere bleibt leer.
 */
export interface ConsultantSearchFields {
  names?: string[];
  positions?: string[];
  companyCode?: string | null;
  companyName?: string | null;
  others?: string[];
}

/**
 * Die Eingabe in einzelne Begriffe: Groß-/Kleinschreibung spielt keine Rolle, Leerraum am
 * Anfang und Ende fällt weg, reiner Leerraum ergibt keinen Begriff und damit keinen Filter.
 */
export function searchTerms(term: string): string[] {
  return (term ?? '')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter((part) => part.length > 0);
}

/**
 * Trifft der Begriff den Anfang eines Wortes? Wörter trennen Leerraum, Komma, Punkt,
 * Bindestrich und Klammern — „Car" findet „Müller-Carstens", nicht „Ricardo". Sonderzeichen
 * im Begriff gelten als normaler Text, nicht als Platzhalter.
 */
export function startsWord(text: string, term: string): boolean {
  if (!text || !term) return false;
  return new RegExp(`(?:^|[\\s,.\\-()\\[\\]])${escapeRegExp(term)}`).test(
    text.toLowerCase(),
  );
}

/** Jeder Begriff muss für sich treffen; ohne Begriff bleibt die Liste vollständig. */
export function matchesScope(
  fields: ConsultantSearchFields,
  scope: SearchScope,
  terms: string[],
): boolean {
  return terms.every((term) => matchesTerm(fields, scope, term));
}

function matchesTerm(
  fields: ConsultantSearchFields,
  scope: SearchScope,
  term: string,
): boolean {
  switch (scope) {
    case 'name':
      return startsAnyWord(fields.names, term);
    case 'pos':
      return containsAny(fields.positions, term);
    case 'firma':
      return (
        (fields.companyCode ?? '').toLowerCase().startsWith(term) ||
        startsWord(fields.companyName ?? '', term)
      );
    case 'alle':
      return (
        startsAnyWord(fields.names, term) || containsAny(fields.others, term)
      );
  }
}

function startsAnyWord(texts: string[] | undefined, term: string): boolean {
  return (texts ?? []).some((text) => startsWord(text, term));
}

function containsAny(texts: string[] | undefined, term: string): boolean {
  return (texts ?? []).some((text) => text.toLowerCase().includes(term));
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
